import { Component } from '@angular/core';
import { ErrorMessages, PageTitle } from '@constants/global.enum';
import { TeamManagementService } from '../team-management.service';
import { GlobalErrorService } from '@services/global-error.service';
import { AuthService } from '@core/auth.service';
import { TeamCompositionListInfo } from '@constants/constants';
import { dropdownValues } from '@interfaces/common.model';

@Component({
  selector: 'app-team-management',
  templateUrl: './team-management.component.html',
  styleUrl: './team-management.component.scss',
})
export class TeamManagementComponent {
  heading = PageTitle.TeamDashboard;
  employeeId: string = '';
  isDataLoaded: boolean = false;
  errorMsg: string = ErrorMessages.NoDataAvailable;
  reporteeData: dropdownValues[] = [];
  selectedDropdownValue: string = 'all';
  selectedDropdownSelection: {
    employeeId: string;
    filterType?: string;
  } | null = null;

  constructor(
    private errorHandler: GlobalErrorService,
    private teamManagementService: TeamManagementService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.teamManagementService
      .getSelectedDropdownValue()
      .subscribe((selection) => {
        this.selectedDropdownSelection = selection;
        this.syncSelectedDropdownValue(selection);
      });

    this.loadTeamCompositionListData();
  }

  /** Team labels for the searchable dropdown; 'all' is covered by the
   *  component's built-in clear row ("All Teams" placeholder). */
  get teamOptions(): string[] {
    return this.reporteeData
      .filter((o) => o.value !== 'all')
      .map((o) => o.label);
  }

  /** Label of the currently selected option, '' when All Teams. */
  get selectedTeamLabel(): string {
    const match = this.reporteeData.find(
      (o) => o.value === this.selectedDropdownValue,
    );
    if (!match || match.value === 'all') return '';
    return match.label;
  }

  /** Bridge the searchable dropdown (string labels) onto the existing
   *  value-based change flow. Empty label = cleared = All Teams. */
  onTeamPick(label: string): void {
    const option = this.reporteeData.find((o) => o.label === label);
    this.onDropdownChange(option ? option.value : 'all');
  }

  onDropdownChange(event: any): void {
    const selectedValue = event?.value || event;
    this.selectedDropdownValue = selectedValue;

    if (selectedValue === 'dr') {
      // Direct Reportees - add filterType
      this.teamManagementService.setSelectedDropdownValue({
        employeeId: this.employeeId,
        filterType: 'DirectReportee',
      });
    } else if (selectedValue === 'all') {
      // All Teams - no filterType
      this.teamManagementService.setSelectedDropdownValue({
        employeeId: this.employeeId,
      });
    } else {
      // Specific employee - no filterType
      this.teamManagementService.setSelectedDropdownValue({
        employeeId: selectedValue,
      });
    }
  }

  async loadTeamCompositionListData(): Promise<void> {
    try {
      const profile = await this.authService.getEmployeeDetails();
      if (profile?.employeeId) {
        this.employeeId = profile?.employeeId;
        this.syncSelectedDropdownValue(this.selectedDropdownSelection);
      }

      this.teamManagementService.getReporteesList(this.employeeId).subscribe({
        next: (res) => {
          this.reporteeData =
            this.teamManagementService.transformReporteesToDropdownOptions(res);

          // Store the transformed data in service for other components
          this.teamManagementService.setReporteeData(this.reporteeData);

          if (!this.selectedDropdownSelection) {
            this.teamManagementService.setSelectedDropdownValue({
              employeeId: this.employeeId,
            });
          }

          this.isDataLoaded = true;
        },
        error: (error) => {
          this.errorHandler.logComponentError(error);
          this.errorMsg =
            TeamCompositionListInfo.FailedToLoadTeamCompositionData;
          this.isDataLoaded = true;
        },
      });
    } catch (error) {
      this.errorMsg = TeamCompositionListInfo.FailedToLoadTeamCompositionData;
    }
  }

  ngOnDestroy(): void {
    this.teamManagementService.setSelectedDropdownValue(null);
  }

  private syncSelectedDropdownValue(
    selection: { employeeId: string; filterType?: string } | null,
  ): void {
    if (!selection) {
      this.selectedDropdownValue = 'all';
      return;
    }

    if (selection.filterType === 'DirectReportee') {
      this.selectedDropdownValue = 'dr';
      return;
    }

    if (this.employeeId && selection.employeeId === this.employeeId) {
      this.selectedDropdownValue = 'all';
      return;
    }

    this.selectedDropdownValue = selection.employeeId;
  }
}
